import AltRouteIcon from '@mui/icons-material/AltRoute';
import CallSplitIcon from '@mui/icons-material/CallSplit'; // re-enable with the DECISION node config below
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import ExtensionIcon from '@mui/icons-material/Extension';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PersonIcon from '@mui/icons-material/Person';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import palette from '/b2b/common/theme/palette';
import { tileColors } from '/b2b/common/colors';

export const NODE_TYPES = {
    APPROVAL: 'APPROVAL',
    DECISION: 'DECISION',
    SWITCH: 'SWITCH',
    DSAR_AUTOMATED_ACTION: 'DSAR_AUTOMATED_ACTION',
    DSAR_MANUAL_TASK: 'DSAR_MANUAL_TASK',
    DSAR_REJECTION: 'DSAR_REJECTION',
    DSAR_READY_FOR_REVIEW: 'DSAR_READY_FOR_REVIEW',
    DSAR_COMPLETION: 'DSAR_COMPLETION',
    MULTIPLE_ACTIONS: 'MULTIPLE_ACTIONS',
};

/**
 * Node types that exist only on the frontend — they are not returned by the
 * backend's node-types endpoint. They are merged into the fetched node-type
 * list so the NodePicker can render them. Shape matches the backend payload
 * ({ workflowNodeType, name }).
 */
export const FRONTEND_ONLY_NODE_TYPES = [
    { workflowNodeType: NODE_TYPES.MULTIPLE_ACTIONS, name: 'Multiple Actions' },
];

export const NODE_TYPE_CATEGORIES = {
    ACTIONS: 'ACTIONS',
    CONTROL_FLOW: 'CONTROL_FLOW',
    DSAR_STATUS: 'DSAR_STATUS',
};

/**
 * Node types that fan the workflow out into multiple condition-labelled branches:
 * DECISION (branches stored as a rules array in its config) and SWITCH (branches
 * live on its outgoing edges; its config only holds the field path it routes on).
 * Use this for STRUCTURAL branching behavior — branch edges, condition chips,
 * subtree deletion, "requires a child". Config-shape-specific logic (the rules
 * array, previous-outcome, edge↔config sync) is DECISION-only and checks
 * `=== NODE_TYPES.DECISION` directly.
 */
export const BRANCHING_NODE_TYPES = new Set([NODE_TYPES.DECISION, NODE_TYPES.SWITCH]);

/** True for the branching node types (DECISION and SWITCH). */
export const isBranchingNodeType = nodeType => BRANCHING_NODE_TYPES.has(nodeType);

/**
 * requiresConnections values:
 *   'none'   — no connection requirements (default)
 *   'parent' — must have at least one incoming connection
 *   'child'  — must have at least one outgoing connection
 *   'both'   — must have at least one incoming AND one outgoing connection
 */
export const REQUIRES_CONNECTIONS = {
    NONE: 'none',
    PARENT: 'parent',
    CHILD: 'child',
    BOTH: 'both',
};

export const NODE_TYPE_RULES = {
    HAS_DIALOG: 'hasDialog',
    IS_DSAR_COMPLETE_STATUS: 'isDsarCompleteStatus',
    REQUIRES_CONNECTIONS: 'requiresConnections',
};

// Default rules applied when a node type doesn't specify a given rule
const DEFAULT_RULES = {
    [NODE_TYPE_RULES.HAS_DIALOG]: false,
    [NODE_TYPE_RULES.IS_DSAR_COMPLETE_STATUS]: false,
    [NODE_TYPE_RULES.REQUIRES_CONNECTIONS]: REQUIRES_CONNECTIONS.NONE,
};

/**
 * Single source of truth for every node type's metadata and rules.
 * Mirrors the shape of a future `workflow_node_types` DB table with a `rules` JSONB column.
 */
export const NODE_TYPE_CONFIG = {
    [NODE_TYPES.DSAR_AUTOMATED_ACTION]: {
        category: NODE_TYPE_CATEGORIES.ACTIONS,
        tileColor: tileColors.purple,
        icon: ExtensionIcon,
        label: 'workflows.nodeTypes.dsarAutomatedAction.name',
        description: 'workflows.nodeTypes.dsarAutomatedAction.description',
        rules: {
            hasDialog: true,
        },
    },
    [NODE_TYPES.DSAR_MANUAL_TASK]: {
        category: NODE_TYPE_CATEGORIES.ACTIONS,
        tileColor: tileColors.blue,
        icon: PersonIcon,
        label: 'workflows.nodeTypes.dsarManualTask.name',
        description: 'workflows.nodeTypes.dsarManualTask.description',
        rules: {
            hasDialog: true,
        },
    },
    [NODE_TYPES.APPROVAL]: {
        category: NODE_TYPE_CATEGORIES.CONTROL_FLOW,
        tileColor: tileColors.cyan,
        icon: ThumbUpIcon,
        label: 'workflows.nodeTypes.approval.name',
        description: 'workflows.nodeTypes.approval.description',
        rules: {
            hasDialog: true,
        },
    },
    [NODE_TYPES.DECISION]: {
        category: NODE_TYPE_CATEGORIES.CONTROL_FLOW,
        tileColor: tileColors.amber,
        icon: CallSplitIcon,
        label: 'workflows.nodeTypes.decision.name',
        description: 'workflows.nodeTypes.decision.description',
        rules: {
            hasDialog: true,
            requiresConnections: REQUIRES_CONNECTIONS.CHILD,
        },
    },
    // Switch is a branching node like Decision (see isBranchingNodeType) and reuses
    // the branch dialog. Its config is { path, branches } (see getSwitchBranches),
    // and those branches are projected onto its outgoing edges — one edge per value
    // (see syncSwitchEdges).
    [NODE_TYPES.SWITCH]: {
        category: NODE_TYPE_CATEGORIES.CONTROL_FLOW,
        tileColor: tileColors.amber,
        icon: AltRouteIcon,
        label: 'workflows.nodeTypes.switch.name',
        description: 'workflows.nodeTypes.switch.description',
        rules: {
            hasDialog: true,
            requiresConnections: REQUIRES_CONNECTIONS.CHILD,
        },
    },
    [NODE_TYPES.MULTIPLE_ACTIONS]: {
        category: NODE_TYPE_CATEGORIES.CONTROL_FLOW,
        color: palette.secondary.main,
        tileColor: tileColors.fuchsia,
        icon: DynamicFeedIcon,
        label: 'workflows.nodeTypes.multipleActions.name',
        description: 'workflows.nodeTypes.multipleActions.description',
        rules: {
            hasDialog: false,
        },
    },
    [NODE_TYPES.DSAR_REJECTION]: {
        category: NODE_TYPE_CATEGORIES.DSAR_STATUS,
        tileColor: tileColors.red,
        icon: CancelIcon,
        label: 'workflows.nodeTypes.dsarRejection.name',
        description: 'workflows.nodeTypes.dsarRejection.description',
        rules: {
            hasDialog: true,
            isDsarCompleteStatus: true,
        },
    },
    [NODE_TYPES.DSAR_READY_FOR_REVIEW]: {
        category: NODE_TYPE_CATEGORIES.DSAR_STATUS,
        tileColor: tileColors.orange,
        icon: FactCheckIcon,
        label: 'workflows.nodeTypes.dsarReadyForReview.name',
        description: 'workflows.nodeTypes.dsarReadyForReview.description',
        rules: {
            isDsarCompleteStatus: true,
        },
    },
    [NODE_TYPES.DSAR_COMPLETION]: {
        category: NODE_TYPE_CATEGORIES.DSAR_STATUS,
        tileColor: tileColors.green,
        icon: CheckCircleIcon,
        label: 'workflows.nodeTypes.dsarCompletion.name',
        description: 'workflows.nodeTypes.dsarCompletion.description',
        rules: {
            hasDialog: true,
            isDsarCompleteStatus: true,
        },
    },
};

/** Node types available on the Basic DSAR plan Actions tab. */
export const BASIC_PLAN_NODE_TYPES = new Set([NODE_TYPES.APPROVAL, NODE_TYPES.DSAR_MANUAL_TASK]);

/**
 * Get a resolved rule value for a node type, falling back to DEFAULT_RULES.
 * @param {string} nodeType - A WORKFLOW_NODE_TYPES value
 * @param {string} rule - A key from DEFAULT_RULES
 */
export const getNodeTypeRule = (nodeType, rule) => {
    const config = NODE_TYPE_CONFIG[nodeType];
    if (!config) {
        return DEFAULT_RULES[rule];
    }
    return config.rules[rule] ?? DEFAULT_RULES[rule];
};

// Convenience accessors that preserve the old lookup-map interface for
// consumers that only need a single property (e.g. WorkflowNode component).
export const NODE_TYPE_TO_CATEGORY = Object.fromEntries(
    Object.entries(NODE_TYPE_CONFIG).map(([type, cfg]) => [type, cfg.category])
);
export const NODE_ICONS = Object.fromEntries(
    Object.entries(NODE_TYPE_CONFIG).map(([type, cfg]) => [type, cfg.icon])
);
export const NODE_LABELS = Object.fromEntries(
    Object.entries(NODE_TYPE_CONFIG).map(([type, cfg]) => [type, cfg.label])
);
export const NODE_DESCRIPTIONS = Object.fromEntries(
    Object.entries(NODE_TYPE_CONFIG).map(([type, cfg]) => [type, cfg.description])
);
export const NODE_TILE_COLORS = Object.fromEntries(
    Object.entries(NODE_TYPE_CONFIG).map(([type, cfg]) => [type, cfg.tileColor])
);

export const AUTOMATION_OPERATIONS = {
    SUMMARIZE: 'SUMMARIZE',
    DELETE: 'DELETE',
};

export const AUTOMATION_OPERATION_CONFIG = {
    [AUTOMATION_OPERATIONS.SUMMARIZE]: {
        operation: 'USER_DATA_SEARCH',
        label: 'dsar.requestActions.summarize',
        description: 'dsar.formDetails.processing.fields.automationConfig.summarize.description',
        background: tileColors.blue.background,
        color: tileColors.blue.icon,
    },
    [AUTOMATION_OPERATIONS.DELETE]: {
        operation: 'DELETE',
        label: 'dsar.requestActions.delete',
        description: 'dsar.formDetails.processing.fields.automationConfig.delete.description',
        background: tileColors.red.background,
        color: tileColors.red.icon,
    },
};

export const WORKFLOW_COMPONENT_TYPE = 'workflow';
export const WORKFLOW_PLACEHOLDER_COMPONENT_TYPE = 'workflowPlaceholder';
export const WORKFLOW_END_COMPONENT_TYPE = 'workflowEnd';
export const WORKFLOW_PARALLEL_COMPONENT_TYPE = 'workflowParallel';

export const APPROVAL_EDGE_CONDITIONS = {
    APPROVED: 'true',
    REJECTED: 'false',
};

export const WORKFLOW_BUILDER_ID = 'workflow-builder-canvas';
export const NODE_WIDTH = 360;
// A "Multiple actions" card holds member cards of NODE_WIDTH, so it renders wider than
// every other node by its own padding (spacing(2) a side) and border (1px a side).
// Layout needs that real width to center the card over its parent/child — centering it
// as a NODE_WIDTH node leaves its edges kinked by half the difference.
export const PARALLEL_CARD_H_PADDING = 17;
export const PARALLEL_CARD_WIDTH = NODE_WIDTH + PARALLEL_CARD_H_PADDING * 2;
export const MIN_ZOOM = 0.1;
export const FIT_VIEW_MAX_ZOOM = 1;
export const REACTFLOW_PRO_OPTIONS = {
    account: 'paid-pro',
    hideAttribution: true,
};

/**
 * Where an inserted node is being placed, set on the insert-picker context and
 * consumed by buildInsertEdges. Every context shape buildInsertEdges handles has
 * a member here — keep the two in sync.
 *   EDGE            — split an existing edge between two nodes:
 *                     { edgeId, source, target, conditionValue?,
 *                       conditionValues?, collapsedEdgeIds? }
 *   APPEND          — append after a source node (a leaf or a branch's dangling end):
 *                     { source, conditionValue?, conditionValues?, parallelMember? }
 *   PARALLEL_SIBLING— add a sibling action into an auto-detected parallel diamond:
 *                     { parentIds, childIds }
 *   PARALLEL_PARENT — insert a shared parent above a parallel group:
 *                     { externalId, memberIds, conditionValue? }
 *   PARALLEL_CHILD  — insert a shared child below a parallel group:
 *                     { externalId, memberIds }
 *   PARALLEL_JOIN   — add the downstream join node every group member flows into:
 *                     { memberIds }
 */
export const INSERT_CONTEXT_TYPES = {
    EDGE: 'edge',
    APPEND: 'append',
    PARALLEL_SIBLING: 'parallelSibling',
    PARALLEL_PARENT: 'parallelParent',
    PARALLEL_CHILD: 'parallelChild',
    PARALLEL_JOIN: 'parallelJoin',
};

/** Limits terminal nodes to only show in the insert picker when the context is append or parallel join. */
export const LEAF_APPEND_INSERT_CONTEXT_TYPES = new Set([
    INSERT_CONTEXT_TYPES.APPEND,
    INSERT_CONTEXT_TYPES.PARALLEL_JOIN,
]);

export const EDGE_COLOR = palette.border;
export const EDGE_CONDITION_LABEL_ID = 'workflow-edge-condition-label';

/** The editing state of the workflow's selected version. */
export const VERSION_MODES = {
    EMPTY: 'empty',
    DRAFT: 'draft',
    PUBLISHED: 'published',
};

/** A version is read-only if it is currently active OR was ever published. */
export const isPublishedVersion = version =>
    Boolean(version?.isPublished || version?.publishedDate);
export const isDraftVersion = version => !isPublishedVersion(version);
